import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { BUSINESS } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/our-work", label: "Our Work" },
  { to: "/about", label: "About" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const linkClass = ({ isActive }) =>
    `text-sm font-semibold transition-colors duration-200 ${
      isActive ? "text-sky-600" : "text-slate-600 hover:text-sky-600"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-sky-100 bg-white/85 backdrop-blur-md">
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 h-16 sm:h-20" aria-label="Main navigation">
        <Link to="/" data-testid="header-nav-logo" className="flex items-center transition-transform duration-300 hover:scale-[1.03]" onClick={() => setOpen(false)}>
          <img src="/logo.png" alt="Julisa's House Cleaning — home" className="h-12 sm:h-14 w-auto" width="600" height="358" />
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkClass} end={l.to === "/"}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={BUSINESS.phoneHref}
            data-testid="header-phone-link"
            className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:bg-slate-50 hover:border-sky-300"
          >
            <Phone className="h-4 w-4 text-sky-600" aria-hidden="true" />
            {BUSINESS.phone}
          </a>
          <Link
            to="/quote"
            data-testid="header-quote-button"
            className="rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-sky-600/20 transition-all duration-200 hover:bg-sky-700 hover:shadow-lg active:scale-[0.98]"
          >
            Get a Free Quote
          </Link>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          className="lg:hidden flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-slate-700"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            key={location.pathname + "-menu"}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-slate-100 bg-white"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-base font-semibold ${
                      isActive ? "bg-sky-50 text-sky-700" : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/quote"
                data-testid="mobile-nav-quote-button"
                onClick={() => setOpen(false)}
                className="mt-2 rounded-xl bg-sky-600 px-4 py-3.5 text-center text-base font-semibold text-white shadow-md shadow-sky-600/20"
              >
                Get a Free Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
